import { Alert, cn } from '@sr/ui-library';
import type { PropsWithChildren, ReactNode } from 'react';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import { userIsInCustomerView } from '../utils/helpers/common';
import { PageHeaderActionsTarget } from './PageHeaderActionsSlot';

const ACTIONS_CLASS_NAME = 'flex flex-shrink-0 flex-wrap items-center gap-2';

export type PageHeaderProps = PropsWithChildren<{
  /** Shown before whatever the page's `PageHeaderActions` render. */
  actions?: ReactNode;
  /** Rendered above the title, inside the header container (e.g. a back link). */
  breadcrumb?: ReactNode;
  /** Merged onto the sticky `<header>`. */
  className?: string;
  title: string;
}>;

/**
 * Sticky page header (banner landmark) plus optional customer-view warning below it.
 */
export const PageHeader = ({
  actions,
  breadcrumb,
  children,
  className,
  title,
}: PageHeaderProps) => {
  // Truthy, not non-null: a conditional `actions={canEdit && <Button />}` that
  // comes out `false` has nothing to show, so it mustn't add an empty area.
  const hasActions = Boolean(actions);

  return (
    <>
      <header
        className={cn(
          // bg keeps content scrolling beneath from being half-blurred.
          'sticky top-0 z-20 w-full bg-background/80 backdrop-blur-lg',
          className,
        )}
      >
        {/* pb-5 pairs with the layout's pt-2 — keeps first-row focus rings
            clear of the sticky edge. */}
        <div className="container flex flex-col gap-4 pb-5 pt-7 @container max-md:px-4">
          {/* Sits in the container's flow rather than above the header, so a
              back link or crumb trail keeps the page's own left edge. */}
          {breadcrumb && <div className="-mb-2">{breadcrumb}</div>}

          <div
            className={cn(
              'flex flex-col items-start justify-between gap-4 @xl:flex-row @xl:items-center',
            )}
          >
            <h1 className="text-lg md:text-xl">{title}</h1>

            {hasActions ? (
              <div className={ACTIONS_CLASS_NAME}>
                {actions}

                {/* `contents`, so a portalled action is a flex item here. */}
                <PageHeaderActionsTarget className="contents" />
              </div>
            ) : (
              // Hidden while empty, or it would stay a flex item and add a gap
              // row under the title below @xl.
              <PageHeaderActionsTarget
                className={cn(ACTIONS_CLASS_NAME, 'empty:hidden')}
              />
            )}
          </div>

          {children && <div className="flex flex-col gap-2">{children}</div>}
        </div>
      </header>

      <div className="container max-md:px-4">
        <CustomerViewBanner />
      </div>
    </>
  );
};

const CustomerViewBanner = () => {
  const { currentUser } = useCurrentUserAuth();
  const [activeWorkspace] = useActiveWorkspace();
  const isCustomerViewSession = userIsInCustomerView(currentUser);

  return (
    isCustomerViewSession && (
      <Alert
        actionButtonProps={{
          children: 'Close customer view',
          onClick: () => window.close(),
        }}
        hasActionButton
        variant="warning"
      >
        Hi <strong>{currentUser?.firstName}</strong>, You are viewing{' '}
        <strong>{activeWorkspace?.workspaceName}</strong> workspace in customer
        view. Some customer actions are not available.
      </Alert>
    )
  );
};
